import { createReducer, createSelector, isAnyOf } from '@reduxjs/toolkit';
import type { RootState } from '../root';
import tinycolor from 'tinycolor2';

import {
  setAPIKey,
  setOrigin,
  addRawParams,
  setRawParams,
  setIntentFiltersForPreview,
  setPreviewIsLiveMode,
  setWidgetInitMethod,
  setAiInstructionsForPreview,
  setContentSnippetFiltersForPreview,
} from 'actions/config/config';

import agathaHelpBtnIcon from 'assets/icons/widget-icon.svg';
import headerLogoIcon from 'assets/icons/header-logo.svg';
import { theme } from '@forethought-technologies/forethought-elements';
import {
  setLastActiveTabId,
  synchronizeState,
} from 'persistence/persistenceBetweenTabs';
import initialState from './initialState';
import { sanitizeHtmlForSecurity } from 'utils/utils';
import mapValues from 'just-map-values';
import type {
  AiInstructionFilters,
  ContentSnippetFilters,
  Font,
  IntentFilters,
  LaunchStyle,
  OnLoadConfig,
  Placement,
  Size,
  WorkflowConfigArgs,
} from 'api/types';
import type { WorkflowsState } from 'reducers/workflows';
import {
  selectConversationId,
  selectCustomConfigParams,
  selectFeatureFlags,
} from 'reducers/workflows';
import { setSelectedIntentIndex } from 'actions/workflows/workflows';
import { selectIsMobile } from 'reducers/main';
import { htmlDecode } from 'utils/htmlDecode';
import { getOverridableConfigValue } from '../../utils/getOverridableConfigValue';

export type WidgetInitMethod = 'url' | 'script';

export interface ConfigState {
  aiInstructionsForPreview: null | AiInstructionFilters[];
  apiKey: string;
  contentSnippetFiltersForPreview: null | ContentSnippetFilters[];
  intentFiltersForPreview: null | IntentFilters;
  lastActiveTabId: string;
  origin: string;
  previewIsLiveMode: null | boolean;
  rawParams?: { [attrName: string]: string | boolean };
  updatedAtMilliseconds: number;
  widgetInitMethod: WidgetInitMethod;
}

const configReducer = createReducer(initialState, builder => {
  builder.addCase(setAPIKey, (state, action) => ({
    ...state,
    apiKey: sanitizeHtmlForSecurity(action.payload),
  }));
  builder.addCase(setOrigin, (state, action) => ({
    ...state,
    origin: sanitizeHtmlForSecurity(action.payload),
  }));
  builder.addCase(addRawParams, (state, action) => ({
    ...state,
    rawParams: {
      ...state.rawParams,
      ...mapValues(action.payload, value =>
        typeof value === 'string' ? sanitizeHtmlForSecurity(value) : value,
      ),
    },
  }));
  builder.addCase(setAiInstructionsForPreview, (state, action) => ({
    ...state,
    aiInstructionsForPreview: action.payload,
  }));
  builder.addCase(setContentSnippetFiltersForPreview, (state, action) => ({
    ...state,
    contentSnippetFiltersForPreview: action.payload,
  }));
  builder.addCase(setIntentFiltersForPreview, (state, action) => ({
    ...state,
    intentFiltersForPreview: action.payload,
  }));
  builder.addCase(setPreviewIsLiveMode, (state, action) => ({
    ...state,
    previewIsLiveMode: action.payload,
  }));

  builder.addCase(setRawParams, (state, action) => ({
    ...state,
    rawParams: mapValues(action.payload, value =>
      typeof value === 'string' ? sanitizeHtmlForSecurity(value) : value,
    ),
  }));

  builder.addCase(setLastActiveTabId, (state, action) => ({
    ...state,
    lastActiveTabId: action.payload,
  }));

  builder.addCase(setWidgetInitMethod, (state, action) => ({
    ...state,
    widgetInitMethod: action.payload,
  }));

  // Update `updatedAtMilliseconds` on every user interaction with the widget.
  // "User interaction" means "all Redux actions, except `setLastActiveTabId`,
  // `synchronizeState`, and `setSelectedIntentIndex`".
  // This is needed for persistence expiration.
  builder.addMatcher(
    action =>
      !isAnyOf(
        setLastActiveTabId,
        synchronizeState,
        setSelectedIntentIndex,
      )(action),
    state => ({
      ...state,
      updatedAtMilliseconds: Date.now(),
    }),
  );

  /**
   * Set 'lastActiveTabId' to the initial value when the user interacts with
   * the widget, should help in cases where the focus event does not fire.
   */
  builder.addMatcher(
    action =>
      [
        'liveChat/sendUserMessage/fulfilled',
        'liveChat/setLiveChatQuery',
        'workflows/setFreeFormQuery',
      ].includes(action.type),
    state => ({
      ...state,
      lastActiveTabId: initialState.lastActiveTabId,
    }),
  );
});

const defaultTabName = 'Customer Support Team';

/**
 * @internal Used in tests, mark as internal for knip.
 */
export const defaultBannerImageConfig: WorkflowConfigArgs['banner_image_config'] =
  {
    alt_text: null,
    image: null,
    is_enabled: false,
    link: null,
  };

/**
 * @internal Used in tests, mark as internal for knip.
 */
export const defaultIntroConfig: WorkflowConfigArgs['intro_config'] = {
  image: {
    enabled: false,
    value: '',
  },
  subtitle: {
    enabled: false,
    value: '',
  },
  title: {
    enabled: false,
    value: '',
  },
};

/**
 * @internal Used in tests, mark as internal for knip.
 */
export const defaultOnLoadConfig: WorkflowConfigArgs['on_load_config'] = {
  kustomer_one_chat: null,
};

/**
 * @internal Used in tests, mark as internal for knip.
 */
export const defaultWidgetArticleSearchConfig: WorkflowConfigArgs['widget_article_search_config'] =
  {
    enabled: false,
  };

const selectApiKey = (state: RootState): ConfigState['apiKey'] =>
  state.config.apiKey;

const selectOrigin = (state: RootState): ConfigState['origin'] =>
  state.config.origin;

const selectRawParams = (state: RootState): ConfigState['rawParams'] =>
  state.config.rawParams ?? {};

const selectIsPositionLeft = (state: RootState): boolean =>
  state.config.rawParams?.['position-x'] === 'left' ||
  selectPlacement(state) === 'bottom_left';

const selectPositionY = (state: RootState): string =>
  String(state.config.rawParams?.['position-y']) || 'bottom';

const selectIsAutonomousAgent = (state: RootState): boolean =>
  typeof state.config.rawParams?.[
    '__experimental-data-ft-autonomous-agent__'
  ] === 'string';

const selectIsDraft = (state: RootState): boolean =>
  Boolean(state.config.rawParams?.['data-intent-id']) ||
  state.config.rawParams?.['data-interactive-email-preview'] === 'true';

const getEmptyStringOverridableConfigValue = ({
  customConfigValue,
  defaultValue = '',
  widgetConfigValue,
}: {
  customConfigValue?: string;
  defaultValue?: string;
  widgetConfigValue?: string | null;
}): string => {
  if (typeof customConfigValue === 'string') {
    return customConfigValue;
  } else if (typeof widgetConfigValue === 'string') {
    return widgetConfigValue;
  } else {
    return defaultValue;
  }
};

// ToDo all these selectors should be in workflows
const selectThemeColor = (state: RootState): string => {
  return getOverridableConfigValue({
    customConfigValue: state.workflows?.customConfigParams
      ?.theme_color as string,
    defaultValue: theme.palette.primary.main,
    widgetConfigValue: state.workflows?.workflowsConfig?.colors?.theme,
  });
};

const selectIsThemeColorDark = (state: RootState): boolean => {
  return tinycolor(selectThemeColor(state)).isDark();
};

const selectBackgroundColor = (state: RootState): string => {
  return tinycolor(selectThemeColor(state)) + '14';
};

const selectIsWhiteColorLegible = (state: RootState): boolean =>
  tinycolor.readability(selectThemeColor(state), theme.palette.colors.white) >=
  4.5;

const selectLegibleTextColor = (state: RootState): string =>
  selectIsWhiteColorLegible(state)
    ? theme.palette.colors.white
    : theme.palette.text.primary;

const selectLegibleElementColor = (state: RootState): string =>
  selectIsWhiteColorLegible(state)
    ? selectThemeColor(state)
    : theme.palette.colors.grey[800];

const selectLegibleIconColor = (state: RootState): string =>
  tinycolor.readability(selectThemeColor(state), theme.palette.colors.white) >=
  3
    ? selectThemeColor(state)
    : theme.palette.colors.grey[800];

const selectLegibleButtonOutlineColor = (state: RootState): string => {
  const themeColor = selectThemeColor(state);

  const candidates = [
    ...tinycolor(themeColor).monochromatic(),
    tinycolor(themeColor).lighten(50),
    tinycolor(themeColor).darken(50),
    ...tinycolor(themeColor).triad(),
    ...tinycolor(themeColor).tetrad(),
    ...tinycolor(themeColor).splitcomplement(),
    tinycolor(themeColor).greyscale(),
  ];

  return (
    candidates
      .find(
        candidate =>
          tinycolor.readability(candidate, theme.palette.colors.white) >= 3 &&
          tinycolor.readability(candidate, themeColor) >= 3,
      )
      ?.toRgbString() || theme.palette.text.primary
  );
};

export const selectLegibleTextColorOnLegibleElementColor = (
  state: RootState,
): string => {
  const elementColor = selectLegibleElementColor(state);
  const readability = tinycolor.readability(
    elementColor,
    theme.palette.colors.white,
  );
  if (readability >= 4.5) {
    return theme.palette.colors.white;
  }
  return theme.palette.text.primary;
};

const selectHasCustomHelpButtonIcon = (state: RootState): boolean => {
  return (
    Boolean(state.workflows?.workflowsConfig?.images?.help_button_hovered) ||
    Boolean(state.workflows?.customConfigParams?.widget_button_image)
  );
};

const selectHelpButtonIcon = (state: RootState): string => {
  return getOverridableConfigValue({
    customConfigValue: state.workflows?.customConfigParams
      ?.widget_button_image as string,
    defaultValue: agathaHelpBtnIcon,
    widgetConfigValue:
      state.workflows?.workflowsConfig?.images?.help_button_hovered,
  });
};

const selectAgentChatIcon = (state: RootState): string => {
  return getOverridableConfigValue({
    customConfigValue: state.workflows?.customConfigParams
      ?.agent_avatar_image as string,
    defaultValue: headerLogoIcon,
    widgetConfigValue: state.workflows?.workflowsConfig?.images?.agent_chat,
  });
};

const selectHeaderLogoIcon = (state: RootState): string => {
  return getOverridableConfigValue({
    customConfigValue: state.workflows?.customConfigParams
      ?.widget_header_image as string,
    defaultValue: headerLogoIcon,
    widgetConfigValue: state.workflows?.workflowsConfig?.images?.header,
  });
};

const selectTabName = (state: RootState): string => {
  return getOverridableConfigValue({
    customConfigValue: state.workflows?.customConfigParams
      ?.widget_header_title as string,
    defaultValue: defaultTabName,
    widgetConfigValue: state.workflows?.workflowsConfig?.text?.tab_name,
  });
};

const selectCustomConfigParamsGreetingLine = (
  state: RootState,
): string | undefined => {
  const greeting = state.workflows?.customConfigParams?.greeting_message as
    | string
    | undefined;
  return htmlDecode(greeting);
};

const selectGreetingLine = (state: RootState): string => {
  return getOverridableConfigValue({
    customConfigValue: selectCustomConfigParamsGreetingLine(state),
    widgetConfigValue: state.workflows?.workflowsConfig?.greeting_line,
  });
};

const selectProactivePromptGreeting = (state: RootState): string => {
  return getOverridableConfigValue({
    customConfigValue: htmlDecode(
      state.workflows?.customConfigParams
        ?.proactive_prompt_greeting_message as string,
    ),
    widgetConfigValue:
      state.workflows?.workflowsConfig?.proactive_chat_greeting,
  });
};

const selectCsatConfigRevamped = (
  state: RootState,
): WorkflowConfigArgs['csat_config'] | undefined => {
  return state.workflows.workflowsConfig?.csat_config;
};

const selectPrivacyConsentConfigState = (
  state: RootState,
): WorkflowConfigArgs['privacy_consent_config'] =>
  state.workflows?.workflowsConfig?.privacy_consent_config ?? null;

const selectPrivacyConsentConfig = createSelector(
  [
    selectPrivacyConsentConfigState,
    (state: RootState) => selectCustomConfigParams(state),
  ],
  (
    privacyConsentConfig,
    customConfigs,
  ): WorkflowConfigArgs['privacy_consent_config'] => {
    if (!privacyConsentConfig) {
      return null;
    }

    return {
      ...privacyConsentConfig,
      call_to_action_label: getOverridableConfigValue({
        customConfigValue: customConfigs?.call_to_action_label as string,
        widgetConfigValue: privacyConsentConfig.call_to_action_label,
      }),
      privacy_policy: getOverridableConfigValue({
        customConfigValue: htmlDecode(customConfigs?.privacy_policy as string),
        widgetConfigValue: privacyConsentConfig.privacy_policy,
      }),
      prompt_header: getOverridableConfigValue({
        customConfigValue: customConfigs?.prompt_header as string,
        widgetConfigValue: privacyConsentConfig.prompt_header,
      }),
      show_when_conversation_starts:
        getOverridableConfigValue({
          customConfigValue:
            customConfigs?.show_when_conversation_starts as string,
          widgetConfigValue: String(
            privacyConsentConfig.show_when_conversation_starts,
          ),
        }) === 'true',
    };
  },
);

export const selectIsPrivacyConsented = (state: RootState): boolean => {
  const showWhenConversationStarts =
    getOverridableConfigValue({
      customConfigValue: state.workflows?.customConfigParams
        ?.show_when_conversation_starts as string,
      widgetConfigValue: String(
        state.workflows?.workflowsConfig?.privacy_consent_config
          ?.show_when_conversation_starts,
      ),
    }) === 'true';

  return (
    !showWhenConversationStarts ||
    Boolean(state.workflows?.consentedPrivacyPolicyHash)
  );
};

export const selectActionToExecAfterPrivacyConsented = (
  state: RootState,
): WorkflowsState['actionToExecAfterPrivacyConsented'] => {
  return state.workflows?.actionToExecAfterPrivacyConsented;
};

const selectBannerImageConfigState = (
  state: RootState,
): WorkflowConfigArgs['banner_image_config'] =>
  state.workflows?.workflowsConfig?.banner_image_config ??
  defaultBannerImageConfig;

const selectBannerImageConfig = createSelector(
  [
    selectBannerImageConfigState,
    (state: RootState) => selectCustomConfigParams(state),
  ],
  (
    bannerImageConfig,
    customConfigs,
  ): WorkflowConfigArgs['banner_image_config'] => ({
    ...bannerImageConfig,
    alt_text: getOverridableConfigValue({
      customConfigValue: customConfigs?.banner_image_alt_text as string,
      widgetConfigValue: bannerImageConfig.alt_text,
    }),
    image: getOverridableConfigValue({
      customConfigValue: customConfigs?.banner_image as string,
      widgetConfigValue: bannerImageConfig.image,
    }),
    is_enabled:
      getOverridableConfigValue({
        customConfigValue: customConfigs?.banner_image_enabled as string,
        widgetConfigValue: String(bannerImageConfig.is_enabled),
      }) === 'true',
    link: getEmptyStringOverridableConfigValue({
      customConfigValue: customConfigs?.banner_image_link as string,
      widgetConfigValue: bannerImageConfig.link,
    }),
  }),
);

const selectPreviewIsLiveMode = (
  state: RootState,
): ConfigState['previewIsLiveMode'] => state.config.previewIsLiveMode;

export const selectIsInteractiveEmailPreview = (state: RootState): boolean =>
  state.config.rawParams?.['data-interactive-email-preview'] === 'true';

export const selectIsInteractiveEmail = (state: RootState): boolean =>
  state.config.rawParams?.['data-forethought-hosted-widget'] === 'true';

export const selectSolveWidgetProduct = (state: RootState): string | null => {
  const product = state.config.rawParams?.['data-solve-widget-product'];

  if (typeof product === 'string') {
    return product;
  }

  return null;
};

export const selectWidgetInitMethod = (state: RootState) =>
  state.config.widgetInitMethod;

const selectIntroConfigState = (
  state: RootState,
): WorkflowConfigArgs['intro_config'] =>
  state.workflows.workflowsConfig?.intro_config ?? defaultIntroConfig;

export const selectIntroConfig = createSelector(
  [
    selectIntroConfigState,
    (state: RootState) => selectCustomConfigParams(state),
  ],
  (introConfig, customConfigs): WorkflowConfigArgs['intro_config'] => ({
    ...introConfig,
    image: {
      enabled:
        getOverridableConfigValue({
          customConfigValue: customConfigs?.intro_image_enabled as string,
          widgetConfigValue: String(introConfig.image.enabled),
        }) === 'true',
      value: getOverridableConfigValue({
        customConfigValue: customConfigs?.intro_image as string,
        widgetConfigValue: introConfig.image.value,
      }),
    },
    subtitle: {
      enabled:
        getOverridableConfigValue({
          customConfigValue: customConfigs?.intro_subtitle_enabled as string,
          widgetConfigValue: String(introConfig.subtitle.enabled),
        }) === 'true',
      value: getOverridableConfigValue({
        customConfigValue: customConfigs?.intro_subtitle as string,
        widgetConfigValue: introConfig.subtitle.value,
      }),
    },
    title: {
      enabled:
        getOverridableConfigValue({
          customConfigValue: customConfigs?.intro_title_enabled as string,
          widgetConfigValue: String(introConfig.title.enabled),
        }) === 'true',
      value: getOverridableConfigValue({
        customConfigValue: customConfigs?.intro_title as string,
        widgetConfigValue: introConfig.title.value,
      }),
    },
  }),
);

export const selectLaunchStyle = (state: RootState): LaunchStyle => {
  return getOverridableConfigValue({
    customConfigValue: state.workflows?.customConfigParams
      ?.launch_style as LaunchStyle,
    widgetConfigValue: state.workflows?.workflowsConfig?.launch_style,
  }) as LaunchStyle;
};

export const selectSize = (state: RootState): Size => {
  return getOverridableConfigValue({
    customConfigValue: state.workflows?.customConfigParams?.size as Size,
    widgetConfigValue: state.workflows?.workflowsConfig?.size,
  }) as Size;
};

export const selectPlacement = (state: RootState): Placement => {
  const launchStyle = selectLaunchStyle(state);
  const placement = getOverridableConfigValue({
    customConfigValue: state.workflows?.customConfigParams
      ?.placement as Placement,
    widgetConfigValue: state.workflows?.workflowsConfig?.placement,
  }) as Placement;

  // valid placements for floating_bar are top, middle, bottom
  if (launchStyle === 'floating_bar') {
    switch (placement) {
      case 'top':
        return placement;
      case 'middle':
        return placement;
      case 'bottom':
        return placement;
      default:
        return 'bottom';
    }
  }

  // valid placements for icon are bottom_left, bottom_right
  if (launchStyle === 'icon') {
    switch (placement) {
      case 'bottom_left':
        return placement;
      case 'bottom_right':
        return placement;
      default:
        return 'bottom_right';
    }
  }

  return placement;
};

export const selectFont = (state: RootState): Font => {
  return getOverridableConfigValue({
    customConfigValue: state.workflows?.customConfigParams?.font as Font,
    widgetConfigValue: state.workflows?.workflowsConfig?.font,
  }) as Font;
};

export const selectWidgetBackgroundColor = (state: RootState): string => {
  return getOverridableConfigValue({
    customConfigValue: state.workflows?.customConfigParams
      ?.background_color as Font,
    defaultValue: theme.palette.colors.white,
    widgetConfigValue: state.workflows?.workflowsConfig?.colors?.background,
  });
};

export const selectUserMessageBubbleColor = (state: RootState): string => {
  const themeColor = selectThemeColor(state);

  return getOverridableConfigValue({
    customConfigValue: state.workflows?.customConfigParams
      ?.user_message_bubble_color as string,
    defaultValue: themeColor,
    widgetConfigValue: null,
  });
};

export const selectShowRestartButtonInFooter = (state: RootState): boolean => {
  const hideNav = state.workflows?.customConfigParams?.hide_nav === 'true';
  const conversationId = selectConversationId(state);
  return Boolean(conversationId && hideNav);
};

export const selectIsFooterDisabled = (state: RootState): boolean => {
  const isBrandingDisabled = getOverridableConfigValue({
    // `config-ft-disable-forethought-branding` is undocumented intentionally,
    // as it potentially could be made a paid feature in the future.
    customConfigValue: state.workflows?.customConfigParams
      ?.disable_forethought_branding as string,
    defaultValue: 'false',
    widgetConfigValue: state.workflows?.workflowsConfig
      ?.disable_forethought_branding
      ? 'true'
      : 'false',
  });
  const isInteractiveEmail = selectIsInteractiveEmail(state);
  const isMobile = selectIsMobile(state);

  return (
    (isBrandingDisabled === 'true' &&
      !selectShowRestartButtonInFooter(state)) ||
    /**
     * For interactive email, show the logo in the widget
     * only on mobile (otherwise show it on the page)
     */
    (isInteractiveEmail && !isMobile)
  );
};

export const selectOnLoadConfig = (state: RootState): OnLoadConfig =>
  state.workflows?.workflowsConfig?.on_load_config ?? defaultOnLoadConfig;

export const selectOnLoadKustomerJwt = createSelector(
  [selectOnLoadConfig],
  (onLoadConfig): string | undefined => {
    return onLoadConfig.kustomer_one_chat?.jwt;
  },
);

const selectWidgetArticleConfigState = (
  state: RootState,
): WorkflowConfigArgs['widget_article_search_config'] =>
  state.workflows.workflowsConfig?.widget_article_search_config ??
  defaultWidgetArticleSearchConfig;

export const selectWidgetArticleConfig = createSelector(
  [
    selectWidgetArticleConfigState,
    (state: RootState) => selectCustomConfigParams(state),
    (state: RootState) => selectFeatureFlags(state),
  ],
  (
    widgetArticleConfig,
    customConfigs,
    featureFlags,
  ): WorkflowConfigArgs['widget_article_search_config'] => ({
    ...widgetArticleConfig,
    enabled:
      featureFlags.includes('widget_article_search') &&
      getOverridableConfigValue({
        customConfigValue:
          customConfigs?.widget_article_search_enabled as string,
        widgetConfigValue: String(widgetArticleConfig.enabled),
      }) === 'true',
  }),
);

export {
  selectApiKey,
  selectOrigin,
  configReducer,
  selectThemeColor,
  selectIsThemeColorDark,
  selectLegibleTextColor,
  selectLegibleElementColor,
  selectLegibleIconColor,
  selectLegibleButtonOutlineColor,
  selectHasCustomHelpButtonIcon,
  selectHelpButtonIcon,
  selectAgentChatIcon,
  selectHeaderLogoIcon,
  selectTabName,
  selectBackgroundColor,
  selectRawParams,
  selectIsPositionLeft,
  selectIsDraft,
  selectCustomConfigParamsGreetingLine,
  selectGreetingLine,
  selectProactivePromptGreeting,
  selectIsAutonomousAgent,
  selectPreviewIsLiveMode,
  selectPrivacyConsentConfig,
  selectBannerImageConfig,
  selectCsatConfigRevamped,
  selectPositionY,
};
